import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { HiPlus, HiX } from "react-icons/hi";
import api, { postRequest } from "../../services/AxiosConnection";
import { Vortex } from "react-loader-spinner";
import NotificationSnackbar from "../../utils/NotificationSnackbar";
import { useNotification } from "../../context/NotificationContext";
import {
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaCalendarAlt,
  FaUserTie,
  FaUsers,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";

const UserManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true); // State for loading indicator
  const { notification, closeNotification } = useNotification(); // Get notification state and closeNotification from context
  const { triggerNotification } = useNotification();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    imageFilePath: null,
    department: "",
    designation: "",
    email: "",
    primary_mobile_no: "",
    secondary_mobile_no: "",
    date_of_birth: "",
    date_of_joining: "",
    reporting_manager: "",
    role_id: 3,
    division_id: user.divisionId,
    siteIds: [],
    createdBy: user.id,
    modifiedBy: user.id,
  });

  const [users, setUsers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Dropdown visibility

  const [availableSites, setAvailableSites] = useState([]); // Example list of available sites

  const initialFormData = {
    username: "",
    password: "",
    confirmPassword: "",
    imageFilePath: null,
    department: "",
    designation: "",
    email: "",
    primary_mobile_no: "",
    secondary_mobile_no: "",
    date_of_birth: "",
    date_of_joining: "",
    reporting_manager: "",
    role_id: 3,
    division_id: user.divisionId,
    siteIds: [],
    createdBy: user.id,
    modifiedBy: user.id,
  };

  // Function to handle opening the popup
  const handleViewDetails = (user) => {
    setSelectedUser(user);
  };

  // Function to close the popup
  const handleClosePopup = () => {
    setSelectedUser(null);
  };

  // Handle checkbox changes for site IDs
  const handleSiteCheckboxChange = (siteId) => {
    setFormData((prevData) => {
      const updatedSiteIds = prevData.siteIds.includes(siteId)
        ? prevData.siteIds.filter((id) => id !== siteId) // Remove if already selected
        : [...prevData.siteIds, siteId]; // Add if not selected
      return {
        ...prevData,
        siteIds: updatedSiteIds,
      };
    });
  };

  useEffect(() => {
    // Fetch division data only if user.role is "Super Admin"
    if (user.role === "Super Admin") {
      const fetchDivisions = async () => {
        try {
          const response = await api.get("/users/get-divisions");
          setDivisions(response.data.divisions); // Store the divisions in state
          setLoading(false);
        } catch (err) {
          // You can log or handle the error if needed, but without setting an error state
          console.error("Failed to fetch divisions", err);
          setLoading(false);
        }
      };

      fetchDivisions();
    } else {
      setLoading(false); // No need to load sites if user is not Admin or Super Admin
    }
  }, [user.role]); // Dependency array makes sure this runs only when user.role changes

  useEffect(() => {
    // Fetch sites data only if user.role is "Admin" or "Super Admin"
    const fetchSites = async () => {
      if (user.role === "Admin" || user.role === "Super Admin") {
        try {
          const response = await api.get("/users/get-sites", {
            params: { userId: user.id }, // Pass user.userid in the query parameters
          });
          setAvailableSites(response.data.sites); // Store the fetched sites in state
          setLoading(false); // Set loading to false after data is fetched
        } catch (err) {
          // console.log("Failed to fetch sites"); // Set error if the request fails
          setLoading(false); // Set loading to false even if there's an error
        }
      } else {
        setLoading(false); // No need to load sites if user is not Admin or Super Admin
      }
    };

    if (user.id) {
      fetchSites();
    }
  }, [user.id, user.role]); // Dependency array ensures it fetches when user.userid or user.role changes

  useEffect(() => {
    // Set role_id and division_id based on user.role
    if (user.role === "Admin") {
      setFormData((prevData) => ({
        ...prevData,
        role_id: 3, // Set to 3 if the role is Admin
        division_id: user.divisionId, // Set division_id from user.divisionId
      }));
      // console.log("Form Data:", formData);
    }

    // Fetch sites data only if user.role is "Admin" or "Super Admin"
    const fetchSites = async () => {
      if (user.role === "Admin" || user.role === "Super Admin") {
        try {
          const response = await api.get("/users/get-sites", {
            params: { userId: user.id }, // Pass user.userid in the query parameters
          });

          setAvailableSites(response.data.sites); // Store the fetched sites in state
          setLoading(false); // Set loading to false after data is fetched
        } catch (err) {
          // console.log("Failed to fetch sites"); // Set error if the request fails
          setLoading(false); // Set loading to false even if there's an error
        }
      } else {
        setLoading(false); // No need to load sites if user is not Admin or Super Admin
      }
    };

    if (user.id) {
      fetchSites();
    }
  }, [user.id, user.role]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/users/get-all-users-by-creator", {
          params: { createdBy: user.id },
        });
        setUsers(response.data.users); // Assuming API returns { users: [...] }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (user?.id) {
      fetchUsers();
    }
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, imageFilePath: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validatePassword = () => {
    if (!isEditing && formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    if (
      isEditing &&
      formData.password &&
      formData.password !== formData.confirmPassword
    ) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Password validation only required when creating or changing password
    if ((!isEditing || formData.password) && !validatePassword()) return;

    try {
      setLoading(true);
      // console.log("Current User ID:", currentUserId);

      const endpoint = isEditing ? `/users/update-user` : "/users/create-user";

      const method = isEditing ? "put" : "post";

      // console.log("Form Data to Send:", formData);

      const response = await api[method](endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 201) {
        triggerNotification(
          `User ${isEditing ? "Updated" : "Created"} Successfully!`,
          "success"
        );

        // Refresh user list
        const usersResponse = await api.get("/users/get-all-users-by-creator", {
          params: { createdBy: user.id },
        });
        setUsers(usersResponse.data.users);

        // Reset form
        if (!isEditing) {
          setFormData({ ...initialFormData });
          setImagePreview(null);
        }
        setShowCreateForm(false);
        setIsEditing(false);
        setCurrentUserId(null);
      }
    } catch (error) {
      console.error("Error creating user:", error);

      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;

        if (status === 400) {
          triggerNotification(
            data.message || "Validation error: Please check your input",
            "error"
          );
          // Handle validation errors
          // alert(data.message || "Validation error: Please check your input");
        } else if (status === 401) {
          triggerNotification("Unauthorized: Please login again", "error");
          // Handle unauthorized access
          // alert("Unauthorized: Please login again");
          navigate("/login");
        } else if (status === 409) {
          triggerNotification(data.details || "User already exists", "error");
          // Handle duplicate user
          // alert(data.details || "User already exists");
        } else {
          triggerNotification(
            "An error occurred while creating the user",
            "error"
          );
          // Handle other server errors
          // alert("An error occurred while creating the user");
        }
      } else if (error.request) {
        triggerNotification(
          "Network error: Please check your internet connection",
          "error"
        );
        // The request was made but no response was received
        // alert("Network error: Please check your internet connection");
      } else {
        triggerNotification("An unexpected error occurred", "error");
        // Something happened in setting up the request
        // alert("An unexpected error occurred");
      }
    } finally {
      // Hide loading state
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    // Confirmation dialog
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this user? This action cannot be undone."
    );
    if (!isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/users/delete-user`, {
        params: { userId: id, modifiedBy: user.id },
      });

      // Refresh the user list
      const response = await api.get("/users/get-all-users-by-creator", {
        params: { createdBy: user.id },
      });
      setUsers(response.data.users);

      triggerNotification("User deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting user:", error);
      triggerNotification(
        error.response?.data?.message || "Failed to delete user",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleEditUser = async (userId) => {
    try {
      setLoading(true);
      const response = await api.get(`/users/get-user?userId=${userId}`);
      const userData = response.data.user;
      // console.log("User Data:", userData);

      // Function to format date into "yyyy-MM-dd"
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Get month and ensure two digits
        const day = String(date.getDate()).padStart(2, "0"); // Get day and ensure two digits
        return `${year}-${month}-${day}`;
      };

      setFormData({
        userId: userData.id,
        username: userData.username,
        password: "", // Empty by default
        confirmPassword: "",
        imageFilePath: userData.imageFilePath,
        department: userData.department,
        designation: userData.designation,
        email: userData.email,
        primary_mobile_no: userData.primary_mobile_no,
        secondary_mobile_no: userData.secondary_mobile_no,
        date_of_birth: formatDate(userData.date_of_birth),
        date_of_joining: formatDate(userData.date_of_joining),
        reporting_manager: userData.reporting_manager,
        role_id: userData.role_id,
        division_id: userData.division_id,
        siteIds: userData.site_ids || [],
        createdBy: user.id,
        modifiedBy: user.id,
      });

      setImagePreview(userData.imageFilePath);
      setIsEditing(true);
      setCurrentUserId(userId);
      setShowCreateForm(true);
    } catch (error) {
      triggerNotification("Failed to fetch user details", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {loading && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-700 opacity-50 flex justify-center items-center z-10">
          <Vortex
            visible={true}
            height="80"
            width="80"
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["black", "gray", "black", "gray", "black", "gray"]}
          />
        </div>
      )}

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        {/* Title */}
        <h1 className="text-3xl font-bold text-center w-full">
          User Management
        </h1>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              if (isEditing) {
                // Reset form data and clear image preview
                setFormData({ ...initialFormData });
                setImagePreview(null);
                setShowCreateForm(false); // Ensure the form is closed before toggling
                setIsEditing(false);
                setCurrentUserId(null);
              } else {
                // Toggle the showCreateForm state only if not editing
                setShowCreateForm((prev) => !prev);
              }
            }}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-105 ${
              showCreateForm
                ? "bg-red-600 hover:bg-red-700 ring-2 ring-red-300"
                : "bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-indigo-200 ring-2 ring-indigo-100"
            }`}
          >
            {showCreateForm ? (
              <HiX className="h-6 w-6 text-white" />
            ) : (
              <HiPlus className="h-6 w-6 text-white" />
            )}
          </button>
          <span
            className={`text-sm font-medium whitespace-nowrap ${
              showCreateForm ? "text-red-600" : "text-indigo-600"
            }`}
          >
            {showCreateForm ? "Close" : "Create User"}
          </span>
        </div>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-white p-8 rounded-xl shadow-lg mb-8 border border-gray-100">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800">
            {isEditing ? "Update User" : "Create New User"}
          </h2>
          {isEditing && (
            <div className="mb-4 text-sm text-gray-500">
              Note: Leave password fields empty to keep current password
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Image Upload */}
            <div className="md:col-span-2 flex justify-center">
              <label className="cursor-pointer group">
                <div className="w-40 h-40 rounded-full border-4 border-dashed border-gray-200 group-hover:border-indigo-500 relative overflow-hidden transition-all">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 group-hover:text-indigo-500">
                      <svg
                        className="w-12 h-12 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Upload Photo</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="text"
                name="username"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                // required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                // required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                  // required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-indigo-600"
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-6 w-6" />
                  ) : (
                    <FaEye className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={validatePassword}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-12"
                  // required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-indigo-600"
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="h-6 w-6" />
                  ) : (
                    <FaEye className="h-6 w-6" />
                  )}
                </button>
              </div>
              {passwordError && (
                <p className="mt-2 text-sm text-red-600">{passwordError}</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <input
                type="text"
                name="department"
                placeholder="Enter department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Designation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Designation
              </label>
              <input
                type="text"
                name="designation"
                placeholder="Enter designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Mobile No.<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="text"
                name="primary_mobile_no"
                placeholder="Enter primary mobile number"
                value={formData.primary_mobile_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Mobile No.
              </label>
              <input
                type="text"
                name="secondary_mobile_no"
                placeholder="Enter Secondary mobile number"
                value={formData.secondary_mobile_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                name="date_of_birth"
                placeholder="Enter date of birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Joining
              </label>
              <input
                type="date"
                name="date_of_joining"
                placeholder="Enter date of joining"
                value={formData.date_of_joining}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reporting Manager
              </label>
              <input
                type="text"
                name="reporting_manager"
                placeholder="Enter reporting manager"
                value={formData.reporting_manager}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Site Selection Dropdown */}
            <div className="relative dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Sites <span className="text-red-600 ml-1">*</span>
              </label>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent flex justify-between items-center"
              >
                <div className="flex flex-wrap gap-1">
                  {formData.siteIds.length > 0 ? (
                    formData.siteIds.map((siteId) => {
                      const site = availableSites.find(
                        (s) => s.siteId === siteId
                      );
                      return (
                        site && (
                          <span
                            key={siteId}
                            className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full"
                          >
                            {site.siteName}
                          </span>
                        )
                      );
                    })
                  ) : (
                    <span className="text-gray-500">Select sites</span>
                  )}
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "transform rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {availableSites.map((site) => (
                    <label
                      key={site.siteId}
                      className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      <input
                        type="checkbox"
                        checked={formData.siteIds.includes(site.siteId)}
                        onChange={() => handleSiteCheckboxChange(site.siteId)}
                        className="mr-2"
                      />
                      {site.siteName} {/* Display the site name */}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Role Selection (Super Admin only) */}
            {user.role === "Super Admin" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role<span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  // required
                >
                  <option value="">Select Role</option>
                  <option value="1">Admin</option>
                  <option value="2">Employee</option>
                </select>
              </div>
            )}

            {/* Division Selection (Super Admin only) */}
            {user.role === "Super Admin" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Division<span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="division_id"
                  value={formData.division_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  // required
                >
                  <option value="">Select Division</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Submit Button */}
            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                {isEditing ? "Update User" : "Create User"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Designation
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.designation}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.role_id === 1
                      ? "Super Admin"
                      : user.role_id === 2
                      ? "Admin"
                      : "Employee"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-2">
                    <button
                      onClick={() => handleEditUser(user.id)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 mr-4"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleViewDetails(user)}
                      className="text-gray-600 hover:text-indigo-900 bg-gray-100 p-2 rounded-full aspect-square flex items-center justify-center"
                    >
                      <IoIosArrowForward />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Popup for User Details */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={handleClosePopup} // Close popup when clicking outside
        >
          <div
            className="bg-white rounded-lg p-8 max-w-md w-full relative shadow-lg"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the popup
          >
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-xl"
            >
              &times;
            </button>

            <div className="flex flex-col items-center space-y-4">
              {/* User Image */}
              {selectedUser.imageFilePath ? (
                <img
                  src={selectedUser.imageFilePath}
                  alt={`${selectedUser.username}'s profile`}
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <FaUserCircle className="w-24 h-24 text-gray-400" />
              )}

              {/* User Details */}
              <div className="space-y-2 text-left">
                <p className="flex items-center">
                  <FaUserTie className="mr-2" />
                  <strong className="mr-2">Username:</strong>
                  {selectedUser.username}
                </p>
                <p className="flex items-center">
                  <FaEnvelope className="mr-2" />
                  <strong className="mr-2">Email:</strong> {selectedUser.email}
                </p>
                <p className="flex items-center">
                  <FaPhone className="mr-2" />
                  <strong className="mr-2">Primary Mobile No:</strong>
                  {selectedUser.primary_mobile_no}
                </p>
                <p className="flex items-center">
                  <FaPhone className="mr-2" />
                  <strong className="mr-2">Secondary Mobile No:</strong>
                  {selectedUser.secondary_mobile_no}
                </p>
                <p className="flex items-center">
                  <FaBirthdayCake className="mr-2" />
                  <strong className="mr-2">Date of Birth:</strong>
                  {new Date(selectedUser.date_of_birth).toLocaleDateString()}
                </p>
                <p className="flex items-center">
                  <FaCalendarAlt className="mr-2" />
                  <strong className="mr-2">Date of Joining:</strong>
                  {new Date(selectedUser.date_of_joining).toLocaleDateString()}
                </p>
                <p className="flex items-center">
                  <FaUsers className="mr-2" />
                  <strong className="mr-2">Reporting Manager:</strong>
                  {selectedUser.reporting_manager}
                </p>
                <p className="flex items-center">
                  <FaMapMarkerAlt className="mr-2" />
                  <strong className="mr-2">Division Name:</strong>
                  {selectedUser.division_name}
                </p>
                <p className="flex items-center flex-wrap">
                  <FaMapMarkerAlt className="mr-2" />
                  <strong className="mr-2">Site Names:</strong>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedUser.site_names.split(",").map((site, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs"
                      >
                        {site.trim()}
                      </span>
                    ))}
                  </div>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
